import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import {
    MdOutlineAdsClick,
    MdOutlinePersonAddAlt,
    MdOutlineTrendingUp,
    MdOutlineVerified,
    MdOutlineGroup,
    MdOutlineMailOutline,
    MdOutlineWarningAmber,
    MdOutlineFileDownload,
} from 'react-icons/md';
import { useToast } from '@podus/ui';
import StatCard from '@/components/StatCard';
import RangeChips from '@/components/RangeChips';
import MetricsChart from '@/components/MetricsChart';
import { api, ApiError, downloadCsv } from '@/lib/api';
import { formatNumber, formatPercent } from '@/lib/format';
import type { AdminMetricsPayload, RangePreset } from '@/types';

/** Programme-wide dashboard: totals, leaderboard and fraud watchlist (FR-32/33/39). */
export default function AdminOverview() {
    const toast = useToast();
    const [preset, setPreset] = useState<RangePreset>('30d');
    const [payload, setPayload] = useState<AdminMetricsPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState<'ambassadors' | 'referrals' | null>(null);

    const load = useCallback(
        async (range: RangePreset) => {
            setLoading(true);
            try {
                const res = await api.get<{ data: AdminMetricsPayload }>(`/ambassador/admin/metrics?preset=${range}`);
                setPayload(res.response.data);
            } catch (err) {
                toast.error(err instanceof ApiError ? err.message : 'Could not load programme metrics');
            } finally {
                setLoading(false);
            }
        },
        [toast],
    );

    useEffect(() => {
        void load(preset);
    }, [preset, load]);

    const handleExport = async (type: 'ambassadors' | 'referrals') => {
        setExporting(type);
        try {
            await downloadCsv(`/ambassador/admin/export?type=${type}`, `podus-${type}.csv`);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not prepare that export');
        } finally {
            setExporting(null);
        }
    };

    const totals = payload?.totals;

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-[22px] font-bold text-secondary">Programme overview</h2>
                    <p className="mt-1 text-[13px] text-[#747474]">
                        Totals exclude referrals still under review, so these numbers match what ambassadors see.
                    </p>
                </div>
                <RangeChips value={preset} onChange={setPreset} disabled={loading} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard
                    label="Total ambassadors"
                    value={formatNumber(totals?.totalAmbassadors ?? 0)}
                    hint={`${formatNumber(totals?.activeAmbassadors ?? 0)} active`}
                    Icon={MdOutlineGroup}
                    loading={loading}
                />
                <StatCard
                    label="Referred signups"
                    value={formatNumber(totals?.signups ?? 0)}
                    Icon={MdOutlinePersonAddAlt}
                    accent
                    loading={loading}
                />
                <StatCard
                    label="Link clicks"
                    value={formatNumber(totals?.clicks ?? 0)}
                    Icon={MdOutlineAdsClick}
                    loading={loading}
                />
                <StatCard
                    label="Conversion rate"
                    value={formatPercent(totals?.conversionRate ?? 0)}
                    Icon={MdOutlineTrendingUp}
                    loading={loading}
                />
                <StatCard
                    label="Activated users"
                    value={formatNumber(totals?.activatedUsers ?? 0)}
                    Icon={MdOutlineVerified}
                    loading={loading}
                />
                <StatCard
                    label="Pending review"
                    value={formatNumber(totals?.pendingReferrals ?? 0)}
                    hint="Excluded from totals"
                    Icon={MdOutlineWarningAmber}
                    loading={loading}
                />
                <StatCard
                    label="Pending invites"
                    value={formatNumber(totals?.pendingInvites ?? 0)}
                    Icon={MdOutlineMailOutline}
                    loading={loading}
                />
                <StatCard
                    label="Paying referrals"
                    value={formatNumber(totals?.payingUsers ?? 0)}
                    Icon={MdOutlineVerified}
                    loading={loading}
                />
            </div>

            <div className="mt-6">
                <MetricsChart data={payload?.series ?? []} loading={loading} />
            </div>

            {/* Fraud watchlist (FR-39) */}
            {payload && payload.watchlist.length > 0 && (
                <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/60 p-5">
                    <div className="flex items-center gap-2">
                        <MdOutlineWarningAmber size={20} className="text-amber-600" />
                        <h3 className="text-[15px] font-bold text-secondary">Fraud watchlist</h3>
                    </div>
                    <p className="mt-1 text-[13px] text-[#7a6a3a]">
                        Signals worth a look. Nothing here is blocked automatically.
                    </p>

                    <ul className="mt-4 flex flex-col gap-3">
                        {payload.watchlist.map((entry) => (
                            <li
                                key={entry.ambassadorId}
                                className="rounded-lg border border-amber-200 bg-white px-4 py-3"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <Link
                                        to={`/admin/ambassadors/${entry.ambassadorId}`}
                                        className="text-[14px] font-bold text-secondary hover:text-primary"
                                    >
                                        {entry.name ?? entry.email}
                                    </Link>
                                    <span className="text-[12px] text-[#747474]">
                                        {formatNumber(entry.confirmedSignups)} signups ·{' '}
                                        {formatPercent(entry.activationRate)} activated
                                    </span>
                                </div>
                                <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                                    {entry.reasons.map((reason) => (
                                        <li key={reason} className="text-[12px] text-[#8a7a4a]">
                                            • {reason}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Leaderboard (FR-33) */}
            <div className="mt-6 rounded-xl border border-[#E0E0E0] bg-white">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#EEEEEE] px-5 py-4">
                    <h3 className="text-[15px] font-bold text-secondary">Ambassador leaderboard</h3>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => handleExport('ambassadors')}
                            disabled={exporting !== null}
                            className="flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-2 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-50"
                        >
                            <MdOutlineFileDownload size={16} />
                            {exporting === 'ambassadors' ? 'Preparing…' : 'Ambassadors CSV'}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleExport('referrals')}
                            disabled={exporting !== null}
                            className="flex items-center gap-1.5 rounded-lg border border-[#E0E0E0] px-3 py-2 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-50"
                        >
                            <MdOutlineFileDownload size={16} />
                            {exporting === 'referrals' ? 'Preparing…' : 'Referrals CSV'}
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left">
                        <thead>
                            <tr className="border-b border-[#EEEEEE] text-[12px] uppercase tracking-wide text-[#9a9a9a]">
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Ambassador
                                </th>
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Code
                                </th>
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Clicks
                                </th>
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Signups
                                </th>
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Activated
                                </th>
                                <th scope="col" className="px-5 py-3 font-semibold">
                                    Conversion
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-6">
                                        <div className="skeleton-shimmer h-10 w-full rounded-lg" />
                                    </td>
                                </tr>
                            )}
                            {!loading && payload?.leaderboard.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-5 py-8 text-center text-[14px] text-[#9a9a9a]">
                                        No ambassadors have activated yet.
                                    </td>
                                </tr>
                            )}
                            {payload?.leaderboard.map((row) => (
                                <tr key={row.id} className="border-b border-[#f5f5f5] last:border-0">
                                    <td className="px-5 py-3.5">
                                        <Link
                                            to={`/admin/ambassadors/${row.id}`}
                                            className="text-[14px] font-semibold text-secondary hover:text-primary"
                                        >
                                            {row.name ?? row.email}
                                        </Link>
                                        <p className="text-[12px] text-[#9a9a9a]">{row.email}</p>
                                    </td>
                                    <td className="px-5 py-3.5 font-mono text-[13px] text-[#4E4D4D]">
                                        {row.code ?? '—'}
                                    </td>
                                    <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                        {formatNumber(row.clicks)}
                                    </td>
                                    <td className="px-5 py-3.5 text-[14px] font-semibold tabular-nums text-secondary">
                                        {formatNumber(row.signups)}
                                    </td>
                                    <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                        {formatNumber(row.activated)}
                                    </td>
                                    <td className="px-5 py-3.5 text-[14px] tabular-nums text-[#4E4D4D]">
                                        {formatPercent(row.conversionRate)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
